import React from 'react';
import { Link } from 'react-router-dom';

const partnerships = [
    {
        title: 'NGO Collaboration',
        description: 'Partnered with Health4All to deliver vaccines to remote communities.',
        status: 'Ongoing',
        icon: (
            <svg className="h-10 w-10 text-malachite-green" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M9 20H4v-2a3 3 0 015.356-1.857M15 10V5a3 3 0 00-6 0v5m6 0a3 3 0 01-6 0m6 0v1a3 3 0 01-6 0v-1" /></svg>
        ),
        badgeColor: 'bg-malachite-green/10 text-malachite-green',
    },
    {
        title: 'Government Project',
        description: 'Working with Abia State to improve rural healthcare infrastructure.',
        status: '2024',
        icon: (
            <svg className="h-10 w-10 text-lochmara-blue" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" /></svg>
        ),
        badgeColor: 'bg-lochmara-blue/10 text-lochmara-blue',
    },
    {
        title: 'Private Sector Partnership',
        description: 'Collaborating with MedTech Innovations to deploy telemedicine solutions.',
        status: 'Completed',
        icon: (
            <svg className="h-10 w-10 text-brand" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 8v8m-4-4h8" /></svg>
        ),
        badgeColor: 'bg-brand/10 text-brand',
    },
];

const Partnerships = () => (
    <div className="flex min-h-screen w-full flex-col bg-gradient-to-br from-blue-50 to-teal-100">
        {/* Hero Section */}
        <section className="from-brand-light relative flex w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br to-blue-100 py-16 md:py-24">
            <div className="relative z-10 mx-auto max-w-3xl text-center">
                <h1 className="animate-fade-in mb-4 font-ubuntu text-4xl font-bold text-lochmara-blue drop-shadow-lg md:text-5xl">Partnerships & Projects</h1>
                <p className="animate-fade-in mb-6 font-redhat text-xl text-gray-700 delay-200 md:text-2xl">Driving impact through collaboration with governments, NGOs, and private sector leaders.</p>
            </div>
            {/* Animated Bubbles and Cross Motif */}
            <svg className="animate-float-slow absolute left-10 top-10 z-0 h-24 w-24 opacity-30" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="40" fill="#0087ca" /></svg>
            <svg className="animate-float-medium absolute right-20 top-24 z-0 h-16 w-16 opacity-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="35" fill="#00b937" /></svg>
            <svg className="animate-float-fast absolute bottom-10 left-1/2 z-0 h-20 w-20 opacity-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="30" fill="#004487" /></svg>
            <svg className="animate-float-cross absolute bottom-16 right-10 z-0 h-16 w-16" viewBox="0 0 64 64" fill="none"><rect x="28" y="8" width="8" height="48" rx="4" fill="#0087ca" /><rect x="8" y="28" width="48" height="8" rx="4" fill="#00b937" /></svg>
            <style>{`
        @keyframes fade-in { 0% { opacity: 0; transform: translateY(40px);} 100% { opacity: 1; transform: translateY(0);} }
        .animate-fade-in { animation: fade-in 1.2s cubic-bezier(.4,0,.2,1) both; }
        .delay-200 { animation-delay: 0.2s; }
        @keyframes float-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        .animate-float-slow { animation: float-slow 7s ease-in-out infinite; }
        @keyframes float-medium { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-30px); } }
        .animate-float-medium { animation: float-medium 5s ease-in-out infinite; }
        @keyframes float-fast { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-40px); } }
        .animate-float-fast { animation: float-fast 3.5s ease-in-out infinite; }
        @keyframes float-cross { 0%, 100% { transform: translateY(0) rotate(-8deg); } 50% { transform: translateY(-18px) rotate(8deg); } }
        .animate-float-cross { animation: float-cross 6s ease-in-out infinite; }
      `}</style>
        </section>

        {/* Partnerships Grid */}
        <section className="w-full px-4 py-12">
            <div className="mx-auto max-w-5xl">
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {partnerships.map((p, idx) => (
                        <div key={p.title} className="group flex cursor-pointer flex-col items-center rounded-2xl bg-white/70 p-8 text-center shadow-lg backdrop-blur-lg transition-transform duration-300 hover:-translate-y-2 hover:shadow-2xl">
                            <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white shadow transition-all duration-300 group-hover:bg-blue-50">
                                {p.icon}
                            </span>
                            <h3 className="mb-2 font-ubuntu text-xl font-bold text-lochmara-blue">{p.title}</h3>
                            <p className="mb-4 font-redhat text-gray-600">{p.description}</p>
                            <span className={`inline-block px-4 py-1 rounded-full text-xs font-bold ${p.badgeColor}`}>{p.status}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* Call to Action Section */}
        <section className="flex w-full flex-col items-center bg-gradient-to-br from-blue-100 to-teal-50 px-4 py-12">
            <h2 className="mb-4 text-center font-ubuntu text-2xl font-bold text-congress-blue md:text-3xl">Want to Partner with Us?</h2>
            <p className="mb-6 max-w-2xl text-center font-redhat text-lg text-gray-700">We’re always open to new collaborations that drive impact. Reach out to explore partnership opportunities!</p>
            <Link to="/contact" className="rounded-full bg-brand px-8 py-4 font-ubuntu text-lg font-semibold text-white shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-brand-green">Contact Us</Link>
        </section>
    </div>
);

export default Partnerships;
